import { ManifestIntegrationProvider } from "src/api/connectApi/manifestTypes";
import { LegacyIntegrationProvider } from "src/api/connectApi/types";
import { LEGACY_PROVIDER_ENDPOINTS } from "src/connections/utils/legacyProviderEndpoints";

export type DisplayableEndpoint = { url: string };

export type ApiEndpointsDisplayData = Record<
  string,
  Record<string, DisplayableEndpoint>
>;

export type ResourceConfigLike = { resource: string; name?: string };

function uniqueDisplayKey(
  existing: ApiEndpointsDisplayData,
  resourceKey: string,
  resourceName: string,
): string {
  return existing[resourceName]
    ? `${resourceName} (${resourceKey})`
    : resourceName;
}

export const getManifestDisplayData = (
  manifest: ManifestIntegrationProvider,
): ApiEndpointsDisplayData => {
  const displayData: ApiEndpointsDisplayData = {};
  const envConfigs = manifest.environment_configs as Record<
    string,
    Record<string, unknown> | undefined
  >;
  const environments = Object.keys(envConfigs);

  for (const [resourceKey, resourceDef] of Object.entries(manifest.resources)) {
    const urlExamples = (
      resourceDef as {
        request_config?: { url_examples?: Record<string, string[]> };
      }
    )?.request_config?.url_examples;
    if (!urlExamples) continue;

    const resourceName =
      (resourceDef as { display_name?: string }).display_name ?? resourceKey;

    const envData: Record<string, DisplayableEndpoint> = {};

    for (const env of environments) {
      if (!urlExamples?.[env]?.length) continue;
      envData[env] = { url: urlExamples[env].join("\n") };
    }

    if (Object.keys(envData).length > 0) {
      displayData[uniqueDisplayKey(displayData, resourceKey, resourceName)] =
        envData;
    }
  }

  return displayData;
};

export const getLegacyApiEndpoints = (
  provider: LegacyIntegrationProvider,
  resourceConfigs?: ResourceConfigLike[],
): ApiEndpointsDisplayData => {
  const displayData: ApiEndpointsDisplayData = {};
  const providerEndpoints = LEGACY_PROVIDER_ENDPOINTS[provider];
  if (!providerEndpoints) return displayData;

  const configuredMap = new Map(
    (resourceConfigs ?? []).map((rc) => [rc.resource, rc.name ?? rc.resource]),
  );

  for (const resourceKey of Object.keys(providerEndpoints)) {
    const envUrls = providerEndpoints[resourceKey];
    if (!envUrls?.production) continue;

    const resourceName = configuredMap.get(resourceKey) ?? resourceKey;
    const envData: Record<string, DisplayableEndpoint> = {
      production: { url: envUrls.production },
    };
    if (envUrls.sandbox) {
      envData.sandbox = { url: envUrls.sandbox };
    }

    displayData[uniqueDisplayKey(displayData, resourceKey, resourceName)] =
      envData;
  }

  return displayData;
};
